import type React from 'react'

import Drawer from '@frontend/components/drawer/Drawer'
import { Sort } from '@frontend/libs/types/product'
import {
  AdjustmentsHorizontalIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import {
  Accordion,
  AccordionItem,
  type AccordionItemIndicatorProps,
  Badge,
  Button,
  type Selection,
} from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import useProductListStore from '../libs/useProductListStore'
import { setFilterToSearchParams } from '../ProductList'
import CheckBoxGroupFilter from './CheckBoxGroupFilter'
import DropdownSorting from './DropdownSorting'

export interface DrawerFilterProps {
  showBrandFilter?: boolean
  showCategoryFilter?: boolean
  showCollectionFilter?: boolean
  showColorFilter?: boolean
  showDiscountFilter?: boolean
  showSizeFilter?: boolean
}

export function DrawerFilter({
  showBrandFilter = true,
  showCategoryFilter = true,
  showCollectionFilter = true,
  showColorFilter = true,
  showDiscountFilter = true,
  showSizeFilter = true,
}: Readonly<DrawerFilterProps>) {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    clearFilter,
    filters,
    init,
    pagination,
    selectedFilters,
    setSelectedFilters,
  } = useProductListStore()

  const [isOpen, setIsOpen] = useState(false)

  const handleOnChange = useCallback(
    (value: string[], key: string) => {
      setSelectedFilters({
        ...selectedFilters,
        [key]: value,
      })
    },
    [selectedFilters, setSelectedFilters],
  )

  const handleApplyFilter = useCallback(() => {
    setSearchParams(setFilterToSearchParams(selectedFilters))
    init()
    setIsOpen(false)
  }, [init, selectedFilters, setSearchParams])

  const handleClearAllFilters = useCallback(() => {
    clearFilter()
    const params = new URLSearchParams()
    params.set('sort', Sort.Default)
    setSearchParams(params)
    setIsOpen(false)
  }, [clearFilter, setSearchParams])

  const handleOnSorting = useCallback(
    (keys: Selection) => {
      init()
      if (keys !== 'all') {
        setSearchParams(
          setFilterToSearchParams({
            ...selectedFilters,
            sort: keys?.values().next().value,
          }),
        )
      }
    },
    [init, selectedFilters, setSearchParams],
  )

  const filterCount = useMemo(() => {
    return (
      selectedFilters.brands.length +
      selectedFilters.collections.length +
      selectedFilters.categories.length +
      selectedFilters.colors.length +
      selectedFilters.sizes.length
    )
  }, [selectedFilters])

  const parsedFilters = filters?.filter((filter) => {
    if (filter.slug === 'brands' && showBrandFilter === false) {
      return false
    }

    if (filter.slug === 'discounts' && showDiscountFilter === false) {
      return false
    }

    if (filter.slug === 'colors' && showColorFilter === false) {
      return false
    }

    if (filter.slug === 'sizes' && showSizeFilter === false) {
      return false
    }

    if (filter.slug === 'categories' && showCategoryFilter === false) {
      return false
    }

    if (filter.slug === 'collections' && showCollectionFilter === false) {
      return false
    }

    return true
  })

  return (
    <>
      <div className="mb-6 flex justify-between">
        <Badge
          className="rounded-none"
          color="primary"
          content={filterCount}
          isInvisible={filterCount == 0}
          showOutline={false}
          size="lg"
          variant="solid"
        >
          <Button
            color="primary"
            isIconOnly
            onPress={() => setIsOpen(true)}
            variant="light"
          >
            <AdjustmentsHorizontalIcon className="h-8 w-8" />
          </Button>
        </Badge>
        <div className="flex items-center divide-x">
          <div className="pr-4 text-sm uppercase">
            {pagination.total} <span>{__('results', 'asavagroup')}</span>
          </div>
          <div>
            <DropdownSorting
              onChange={handleOnSorting}
              value={new Set([selectedFilters.sort])}
            />
          </div>
        </div>
      </div>
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} position="left">
        <Drawer.Header>
          <h5 className="mb-0 text-xl font-medium uppercase leading-normal">
            {__('Filters', 'asavagroup')}
          </h5>
        </Drawer.Header>
        <Drawer.Content>
          <div className="h-[calc(100dvh_-_13rem)] overflow-y-auto">
            <div className="grid grid-cols-1 divide-y [&>*]:py-4">
              <Accordion
                className="p-0"
                itemClasses={{
                  content: 'p-0 pb-4',
                  indicator: 'data-[open="true"]:!rotate-0',
                  title: 'text-base font-medium uppercase',
                }}
                selectionMode="multiple"
              >
                {parsedFilters?.map((filter) => (
                  <AccordionItem
                    aria-label={filter.title}
                    indicator={Indicator}
                    key={filter.slug}
                    title={
                      <div className="flex justify-between">
                        {filter.title}{' '}
                        <NumberCount
                          number={
                            selectedFilters[
                              filter.slug as
                                | 'brands'
                                | 'categories'
                                | 'collections'
                                | 'colors'
                                | 'discounts'
                                | 'sizes'
                            ].length
                          }
                        />
                      </div>
                    }
                  >
                    <CheckBoxGroupFilter
                      onChange={(value) => handleOnChange(value, filter.slug)}
                      options={filter.terms}
                      value={
                        selectedFilters[
                          filter.slug as
                            | 'brands'
                            | 'categories'
                            | 'collections'
                            | 'colors'
                            | 'discounts'
                            | 'sizes'
                        ] ?? []
                      }
                    />
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </Drawer.Content>
        <Drawer.Footer>
          <div className="flex justify-between gap-2">
            <Button
              fullWidth
              onClick={handleClearAllFilters}
              size="lg"
              variant="bordered"
            >
              {__('Clear All', 'asavagroup')}
            </Button>
            <Button
              color="primary"
              fullWidth
              onClick={handleApplyFilter}
              size="lg"
              variant="solid"
            >
              {__('Apply', 'asavagroup')}
            </Button>
          </div>
        </Drawer.Footer>
      </Drawer>
    </>
  )
}

export default DrawerFilter

const Indicator = ({ isOpen }: AccordionItemIndicatorProps) =>
  isOpen ? <MinusIcon className="h-8 w-8" /> : <PlusIcon className="h-8 w-8" />

function NumberCount({
  number,
}: { readonly number: number }): React.JSX.Element {
  if (number > 0) {
    return (
      <span className="inline-block bg-primary px-2 py-1 text-xs leading-none text-white">
        {number}
      </span>
    )
  }
  return <></>
}
